"use client"

import styled from "@emotion/styled"
import {
  CarImagePlaceholder,
  CarType,
  PreviouslyViewed,
  useComparison,
} from "@eurodrive/ui"
import { getUmbracoMediaUrl } from "@eurodrive/utils/getUmbracoMediaUrl"
import { getNonUmbracoUrl } from "@eurodrive/utils"
import { vehicleCardClicked, vehicleCardViewed } from "@eurodrive/utils/gtm"
import {
  Box,
  Button,
  Checkbox as CheckboxComponent,
  Typography,
} from "@mui/material"
import { LeasingCarDto, SaleCarDto } from "api"
import Image from "next/image"
import Link from "next/link"
import { useEffect, useRef } from "react"
import Checkbox from "../../ui/icons/Checkbox"
import CheckboxActive from "../../ui/icons/CheckboxActive"
import { sharedColors as colors } from "../../ui/theme/colors"
import { mediaQueries } from "../../ui/theme/mediaQueries"

const ResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const CarsGrid = styled.ul`
  display: grid;
  gap: 12px;
  width: 100%;
  grid-template-columns: 1fr;
  list-style: none;
  padding: 0;
  margin: 0;

  ${mediaQueries.desktop} {
    grid-template-columns: repeat(3, 1fr);
  }
`

const CarCard = styled.li`
  width: 100%;
  border: 1px solid #ece9e9;
  border-radius: 8px;
  overflow: hidden;
  background: ${colors.white};
  position: relative;
`

const ImageContainer = styled.div`
  height: 195px;
  position: relative;

  display: flex;
  align-items: flex-start;
  border-bottom: 1px solid #f3f3f3;
`

const ContainerLayout = styled.div`
  z-index: 2;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
`

const Content = styled.div`
  display: flex;
  width: 100%;
  padding: 16px;
  gap: 8px;
  justify-content: space-between;
`

const CheckboxContainer = styled.div`
  position: absolute;
  top: 16px;
  inset-inline-start: 16px;
  z-index: 3;
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
  padding: 4px 8px;
  background-color: rgba(255, 255, 255, 0.5);
`

const StyledComparisonCheckbox = styled(CheckboxComponent)`
  &.Mui-focusVisible {
    border: 1px dashed white;
    outline: 2px solid black;
    outline-offset: 2px;
    border-radius: 2px;
  }
`

const PromoTag = styled.div`
  position: absolute;
  bottom: 16px;
  right: 16px;
  background: #8c0000;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 400;
  z-index: 3;
`

const BottomContent = styled.div`
  display: flex;
  padding: 16px;
  gap: 16px;
  flex-direction: column;
  text-align: left;
`

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #f3f3f3;
`

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  text-align: left;
  gap: 8px;
`

const ChipsFlex = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const Chip = styled.div`
  display: flex;
  padding: 4px 8px;
  justify-content: center;
  gap: 10px;
  border-radius: 4px;
  background: #f3f3f3;
`

const PriceContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const LoadMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 24px 0;
`

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 0;
  width: 100%;
  gap: 16px;
`

const EmptyMessageContainer = styled.div`
  text-align: center;
  max-width: 600px;
`

const SrOnly = styled.p`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`

const StyledLink = styled(Link)`
  color: black;
`

const Discount = styled.div`
  display: flex;
  width: 91.569px;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 0 8px 0 0;
  background: var(--Eurodrive-red, #8c0000);
`
interface CarResultsProps {
  cars: (SaleCarDto | LeasingCarDto)[]
  totalCount: number
  currentCount: number
  onLoadMore: () => void
  hasMoreItems?: boolean
  brands?: {
    image: string
    title: string
    logo: string
    url: string
  }[]
  loading?: boolean
  isLeasing?: boolean
  comparisonBlock: any
  leasingImageMap?: Record<string, string>
}

interface CarItemProps {
  car: SaleCarDto | LeasingCarDto
  isSelling: boolean
  carType: CarType
  leasingImageMap?: Record<string, string>
}

function CarItem({ car, isSelling, carType, leasingImageMap }: CarItemProps) {
  const comparison = useComparison()

  // Generate the correct URL based on car type
  const carUrl = isSelling
    ? `/cars/${(car as SaleCarDto).vehicleNumber}`
    : `/leasing/${(car as LeasingCarDto).manufacturerCode}-${
        (car as LeasingCarDto).modelCode
      }`

  // Generate car ID for selection tracking
  const carId = isSelling
    ? `sale-${(car as SaleCarDto).vehicleNumber}`
    : `leasing-${(car as LeasingCarDto).manufacturerCode}-${
        (car as LeasingCarDto).modelCode
      }`

  // Get car name
  const carName = car.modelName || car.carZone?.name?.modelNameEn || ""

  // Get car image — API first, then Umbraco fallback for leasing cars
  const mainImage = car.detailedImages?.images?.find((el) => el.label === "SRP")
  let carImage = mainImage?.url || car.mainImage
  if (!carImage && !isSelling && leasingImageMap) {
    const lc = car as LeasingCarDto
    const key = `${lc.manufacturerCode}-${lc.modelCode}`
    const umbracoImage = leasingImageMap[key]
    if (umbracoImage) {
      carImage = getUmbracoMediaUrl(getNonUmbracoUrl(umbracoImage))
    }
  }

  // Get brand icon - could be improved with proper brand mapping
  const brandIcon = "" // TODO: Add proper brand icon mapping

  // Generate chips based on car type and properties
  const generateChips = (): string[] => {
    const chips: string[] = []

    if (!isSelling) {
      const leasingCar = car as LeasingCarDto
      chips.push("ליסינג")

      if (leasingCar.engineCapacity) {
        chips.push(`${leasingCar.engineCapacity}cc`)
      }

      if (
        leasingCar.marketingFeatures &&
        leasingCar.marketingFeatures.length > 0
      ) {
        chips.push(leasingCar.marketingFeatures[0])
      }

      if (leasingCar.manufacturerName) {
        chips.push(leasingCar.manufacturerName)
      }
    } else {
      const saleCar = car as SaleCarDto

      // Hand status - convert from enum to Hebrew
      const handMap: Record<string, string> = {
        None: "חדש",
        First: "יד ראשונה",
        Second: "יד שנייה",
        Third: "יד שלישית",
        Fourth: "יד רביעית",
        FifthAndAbove: "יד חמישית ומעלה",
      }

      if (saleCar.hand && saleCar.hand !== "None") {
        chips.push(handMap[saleCar.hand] || saleCar.hand)
      } else if (saleCar.carClassification === "NewCar") {
        chips.push("חדש")
      }

      // Mileage
      if (saleCar.mileage && saleCar.mileage > 150) {
        chips.push(`${saleCar.mileage.toLocaleString()} ק״מ`)
      } else if (saleCar.carClassification === "NewCar") {
        chips.push("0 ק״מ")
      }
      // Drive type (fuel type)
      if (saleCar.driveTypeDescription) {
        chips.push(saleCar.driveTypeDescription)
      }

      // Transmission type
      if (saleCar.transmissionTypeDescription) {
        chips.push(saleCar.transmissionTypeDescription)
      }
    }

    return chips.slice(0, 4) // Limit to 4 chips
  }

  const chips = generateChips()

  // Get prices (advertised/sale and original)
  const { displayPrice, originalPrice } = isSelling
    ? {
        displayPrice:
          (car as SaleCarDto).discountedPrice ||
          (car as SaleCarDto).advertisedPrice ||
          "0",
        originalPrice:
          (car as SaleCarDto).discountedPrice &&
          (car as SaleCarDto).advertisedPrice !==
            (car as SaleCarDto).discountedPrice
            ? (car as SaleCarDto).advertisedPrice
            : undefined,
      }
    : {
        displayPrice:
          (
            car as LeasingCarDto
          ).monthlyPaymentSalePriceIncludingVat?.toString() ||
          (car as LeasingCarDto).monthlyPaymentPriceIncludingVat?.toString() ||
          "0",
        originalPrice:
          (car as LeasingCarDto).monthlyPaymentSalePriceIncludingVat &&
          (car as LeasingCarDto).monthlyPaymentPriceIncludingVat !==
            (car as LeasingCarDto).monthlyPaymentSalePriceIncludingVat
            ? (car as LeasingCarDto).monthlyPaymentPriceIncludingVat?.toString()
            : undefined,
      }

  // Check if car is selected
  const isSelected = comparison.isCarSelected(carId)

  // Check if car has promotion tag (במבצע)
  const hasPromotion = car.tags?.some(
    (tag) =>
      tag.vehicleTagForWebsiteDescription.includes("במבצע") ||
      tag.vehicleTagForWebsiteDescription.includes("מבצע"),
  )

  // Handle checkbox click
  const handleCheckboxChange = (e: React.MouseEvent) => {
    e.preventDefault() // Prevent navigation
    e.stopPropagation() // Prevent event bubbling

    if (isSelected) {
      comparison.removeCar(carId)
    } else {
      comparison.addCar(car, carType)
    }
  }

  // Track card click
  const handleCardClick = () => {
    vehicleCardClicked(carId, isSelling ? "Sale" : "Leasing", "search_results")
  }

  // Track card view
  const cardRef = useRef<HTMLLIElement>(null)
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            vehicleCardViewed(
              carId,
              isSelling ? "Sale" : "Leasing",
              displayPrice,
            )
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.5 },
    )

    if (cardRef.current) {
      observer.observe(cardRef.current)
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current)
      }
    }
  }, [carId, isSelling, displayPrice])

  const carLinkLabel = `${carName}, מחיר ${displayPrice} ש״ח${originalPrice ? `, מחיר מקורי ${originalPrice} ש״ח` : ""}, ${chips.join(", ")}`

  return (
    <CarCard ref={cardRef}>
      <CheckboxContainer>
        <StyledComparisonCheckbox
          icon={<Checkbox />}
          checkedIcon={<CheckboxActive />}
          checked={isSelected}
          onClick={handleCheckboxChange}
          inputProps={{
            "aria-label": `${isSelected ? "הסר" : "הוסף"} ${carName} להשוואה. כרגע ${comparison.count} מתוך 3 רכבים מקסימום נבחרו להשוואה`,
          }}
        />
        <Typography variant="body3" aria-hidden="true">
          להשוואה
        </Typography>
      </CheckboxContainer>
      <Link
        href={carUrl}
        style={{ textDecoration: "none", color: "inherit" }}
        onClick={handleCardClick}
        aria-label={carLinkLabel}
      >
        <ImageContainer>
          <ContainerLayout>
            <Content>
              {brandIcon && (
                <Image
                  src={getUmbracoMediaUrl(brandIcon)}
                  alt=""
                  width={18}
                  height={18}
                  style={{ objectFit: "contain", marginInlineStart: "auto" }}
                />
              )}
            </Content>
            {originalPrice && (
              <Discount>
                <Typography variant="body2" color="white">
                  במבצע
                </Typography>
              </Discount>
            )}
          </ContainerLayout>
          {hasPromotion && <PromoTag>במבצע</PromoTag>}
          {carImage ? (
            <Image
              src={getUmbracoMediaUrl(carImage)}
              alt={carName}
              fill
              sizes="(min-width: 900px) 350px, 50vw"
              style={{ objectFit: "cover" }}
            />
          ) : (
            <CarImagePlaceholder />
          )}
        </ImageContainer>
        <BottomContent>
          <DetailsContainer>
            <Typography variant="body3" component={"h3"} fontWeight={400}>
              {carName}
            </Typography>
            <ChipsFlex>
              {chips.map((chip, i) => (
                <Chip key={i}>
                  <Typography variant="body2">{chip}</Typography>
                </Chip>
              ))}
            </ChipsFlex>
          </DetailsContainer>
          <Divider />
          <PriceContainer>
            <Typography variant="body1">
              מחיר ₪{parseInt(displayPrice).toLocaleString()}
            </Typography>
            {originalPrice && (
              <Typography
                variant="body3"
                sx={{ textDecoration: "line-through" }}
              >
                ₪{parseInt(originalPrice).toLocaleString()}
              </Typography>
            )}
          </PriceContainer>
        </BottomContent>
      </Link>
    </CarCard>
  )
}

export default function CarResults({
  cars,
  totalCount,
  currentCount,
  onLoadMore,
  hasMoreItems,
  brands = [],
  loading = false,
  isLeasing,
  comparisonBlock,
  leasingImageMap,
}: CarResultsProps) {
  const hasMore = hasMoreItems ?? currentCount < totalCount
  const carType: CarType = isLeasing ? "leasing" : "sale"

  // Show loading state before evaluating empty results
  if (loading && cars.length === 0) {
    return (
      <ResultsContainer>
        <CarsGrid>
          {Array.from({ length: 6 }).map((_, i) => (
            <Box
              key={i}
              sx={{
                border: "1px solid #ece9e9",
                borderRadius: "8px",
                background: colors.white,
                height: 320,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box sx={{ height: 195, bgcolor: "#f3f3f3" }} />
              <Box
                sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}
              >
                <Box
                  sx={{
                    width: "60%",
                    height: 16,
                    bgcolor: "#f3f3f3",
                    borderRadius: 1,
                  }}
                />
                <Box
                  sx={{
                    width: "40%",
                    height: 16,
                    bgcolor: "#f3f3f3",
                    borderRadius: 1,
                  }}
                />
                <Box
                  sx={{
                    width: "80%",
                    height: 16,
                    bgcolor: "#f3f3f3",
                    borderRadius: 1,
                  }}
                />
              </Box>
            </Box>
          ))}
        </CarsGrid>
      </ResultsContainer>
    )
  }

  // Show empty state when no cars found (after loading completed)
  if (!loading && cars.length === 0) {
    return (
      <ResultsContainer>
        <EmptyStateContainer role="alert" aria-live="polite">
          <EmptyMessageContainer>
            <Typography variant="h4" sx={{ textTransform: "uppercase" }}>
              הרכב שחיפשת לא נמצא,{" "}
              <StyledLink href={"/contact-us"}>דבר איתנו</StyledLink> ונתאים לך
              רכב מדויק עבורך.
            </Typography>
          </EmptyMessageContainer>
        </EmptyStateContainer>
        <PreviouslyViewed
          comparisonBlock={comparisonBlock}
          brands={brands}
          searchPage={true}
          title="הרכבים האחרונים שראיתם"
          noPadding
        />
      </ResultsContainer>
    )
  }

  return (
    <ResultsContainer>
      <SrOnly role="alert">
        התקבלו {totalCount} תוצאות, נווט אליהם עם מקש TAB
      </SrOnly>
      <Typography variant="h3">{isLeasing ? "ליסינג" : "רכבים"}</Typography>
      <CarsGrid
        role="list"
        aria-label={`רשימת ${isLeasing ? "רכבי ליסינג" : "רכבים"}, ${totalCount} רכבים נמצאו`}
      >
        {cars.map((car, index) => {
          const isSelling = "vehicleNumber" in car
          const carId = isSelling
            ? `sale-${(car as SaleCarDto).vehicleNumber}`
            : `leasing-${(car as LeasingCarDto).manufacturerCode}-${
                (car as LeasingCarDto).modelCode
              }`

          return (
            <CarItem
              key={carId}
              car={car}
              isSelling={isSelling}
              carType={carType}
              leasingImageMap={leasingImageMap}
            />
          )
        })}
      </CarsGrid>
      {hasMore && (
        <LoadMoreContainer>
          <Button variant="primary" size="large" onClick={onLoadMore}>
            טען עוד
          </Button>
        </LoadMoreContainer>
      )}
    </ResultsContainer>
  )
}
